import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Obtiene los números de tienda asignados a un administrador
 * @param {string} serviceId - ID del servicio
 * @param {Array} tiendaIds - IDs o referencias de tiendas del administrador
 * @returns {Promise<Array>} - Lista de objetos con información de tiendas
 */
export const fetchTiendasAdmin = async (serviceId, tiendaIds) => {
  if (!serviceId) {
    console.error("fetchTiendasAdmin: ID de servicio no proporcionado");
    return [];
  }

  if (!tiendaIds) {
    console.error("fetchTiendasAdmin: tiendaIds es null o undefined");
    return [];
  }

  // Aseguramos que tiendaIds sea un array
  const tiendaIdsArray = Array.isArray(tiendaIds) ? tiendaIds : [tiendaIds];
  
  if (tiendaIdsArray.length === 0) {
    console.log("fetchTiendasAdmin: No hay tiendas para procesar");
    return [];
  }

  console.log("fetchTiendasAdmin - Ejecutando con:", { 
    serviceId, 
    tiendaIdsCount: tiendaIdsArray.length 
  });

  try {
    // Extraer IDs de tienda, manejando diferentes formatos posibles
    const processedIds = tiendaIdsArray.map(item => {
      // Si es un string, usarlo directamente
      if (typeof item === 'string') {
        return item;
      }
      
      // Si es un objeto con propiedad 'id', usar esa propiedad
      if (typeof item === 'object' && item !== null) {
        if ('id' in item) return item.id;
        if ('tiendaId' in item) return item.tiendaId;
        
        // Buscar cualquier propiedad que parezca un ID
        const possibleIdProps = Object.keys(item).filter(key => 
          key.toLowerCase().includes('id') || 
          key.toLowerCase().includes('tienda')
        );
        
        if (possibleIdProps.length > 0) {
          const idProp = possibleIdProps[0];
          console.log(`Usando propiedad ${idProp} como ID`);
          return item[idProp];
        }
      }
      
      // Intentar convertir a string como último recurso
      try {
        return String(item);
      } catch (e) {
        console.warn("No se pudo procesar el ID de tienda:", item);
        return null;
      }
    }).filter(Boolean); // Eliminar valores nulos
    
    if (processedIds.length === 0) {
      console.warn("No se pudieron extraer IDs válidos de tiendas");
      return [];
    }
    
    console.log("IDs de tienda procesados:", processedIds);
    
    // Obtener información de cada tienda
    const tiendasInfo = await Promise.all(
      processedIds.map(async (tiendaId) => {
        try {
          // Primero intentamos obtener el documento directamente
          const tiendaRef = doc(db, "servicios", serviceId, "tiendas", tiendaId);
          const tiendaDoc = await getDoc(tiendaRef);
          
          // Si el documento existe, usamos sus datos
          if (tiendaDoc.exists()) {
            const data = tiendaDoc.data();
            return {
              id: tiendaId,
              numero: data.numTienda,
              nombre: data.nombreTienda || `Servicio ${data.numTienda}`,
              cliente: data.nombreCliente
            };
          }
          
          // Si no existe, intentamos buscar por numTienda
          console.log(`Documento con ID ${tiendaId} no encontrado, intentando buscar por numTienda`);
          
          // Verificar si tiendaId podría ser un número de tienda
          const isNumeric = !isNaN(tiendaId) || !isNaN(parseInt(tiendaId));
          
          if (isNumeric) {
            const numTienda = parseInt(tiendaId);
            const tiendasQuery = query(
              collection(db, "servicios", serviceId, "tiendas"),
              where("numTienda", "==", numTienda)
            );
            
            const querySnapshot = await getDocs(tiendasQuery);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              const data = doc.data();
              
              return {
                id: doc.id,
                numero: data.numTienda,
                nombre: data.nombreTienda || `Servicio ${data.numTienda}`,
                cliente: data.nombreCliente
              };
            }
          }
          
          // Como último recurso, devolvemos un objeto con la información mínima
          console.log(`No se encontró información para la tienda ${tiendaId}`);
          return {
            id: tiendaId,
            numero: parseInt(tiendaId) || tiendaId,
            nombre: `Servicio ${tiendaId}`,
            cliente: null
          };
        } catch (error) {
          console.error(`Error al obtener información de la tienda ${tiendaId}:`, error);
          return null;
        }
      })
    );
    
    // Filtrar tiendas nulas y duplicadas (por número de tienda)
    const tiendasValidas = tiendasInfo.filter(Boolean);
    const tiendasUnicas = [];
    const numerosVistos = new Set();
    
    tiendasValidas.forEach(tienda => {
      if (tienda.numero && !numerosVistos.has(tienda.numero)) {
        numerosVistos.add(tienda.numero);
        tiendasUnicas.push(tienda);
      }
    });
    
    console.log("fetchTiendasAdmin - Tiendas recuperadas:", tiendasUnicas);
    
    return tiendasUnicas;
  } catch (error) {
    console.error("Error al obtener números de tiendas:", error);
    return [];
  }
};